/**
 * Getting permission and a push subscription out of the browser.
 *
 * iOS gives exactly one chance at the permission prompt. Dismiss it and
 * `Notification.requestPermission` resolves "denied" forever after, with no
 * way back except iOS Settings — which is why the caller explains what the
 * prompt is before showing it, and why "denied" is reported as a state with
 * instructions rather than as a failure.
 *
 * It also only works at all in an installed app: Safari on iOS does not offer
 * push to a page in a tab.
 */
import { VAPID_PUBLIC_KEY } from "@/config";

export type PushSupport = "ok" | "unsupported" | "needs-install";

/**
 * Base64url, as the VAPID key is published, into the bytes the API wants.
 *
 * Returns the underlying buffer rather than the view: the DOM types accept a
 * BufferSource, and a Uint8Array over a SharedArrayBuffer-capable ArrayBufferLike
 * does not satisfy it.
 */
export function decodeKey(base64url: string): ArrayBuffer {
  const padded = base64url.replaceAll("-", "+").replaceAll("_", "/");
  const binary = atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, "="));
  const bytes = Uint8Array.from(binary, (char) => char.codePointAt(0) ?? 0);
  return bytes.buffer as ArrayBuffer;
}

export function supportFor(): PushSupport {
  if (!("serviceWorker" in navigator) || !("PushManager" in globalThis)) {
    // iOS only exposes PushManager to an installed app, so an iPhone in Safari
    // lands here and is told to install rather than that its browser is old.
    const standalone =
      globalThis.matchMedia("(display-mode: standalone)").matches ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true;
    return standalone ? "unsupported" : "needs-install";
  }
  return "ok";
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

/**
 * Must be called from a user gesture. Resolves to the subscription, or null if
 * permission was refused.
 */
export async function subscribe(): Promise<PushSubscription | null> {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return null;
  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  if (existing !== null) return existing;
  return registration.pushManager.subscribe({
    // Required by every browser, and the only value it accepts.
    userVisibleOnly: true,
    applicationServerKey: decodeKey(VAPID_PUBLIC_KEY),
  });
}

export async function unsubscribe(): Promise<void> {
  const subscription = await currentSubscription();
  await subscription?.unsubscribe();
}
